export enum ApplicationStatus {
  PAYMENT_REMINDER = "payment_reminder",
  GATED_ACCESS = "gated_access",
  ACTIVE = "active",
  SEAT_LIMIT_EXCEEDED = "seat_limit_exceeded",
}

export enum QueryHistoryType {
  DISABLED = "disabled",
  ANONYMIZED = "anonymized",
  NORMAL = "normal",
}

export interface Settings {
  anonymous_user_enabled: boolean;
  invite_only_enabled: boolean;
  anonymous_user_path?: string;
  maximum_chat_retention_days?: number | null;
  company_name?: string | null;
  company_description?: string | null;
  notifications: Notification[];
  needs_reindexing: boolean;
  gpu_enabled: boolean;
  application_status: ApplicationStatus;
  auto_scroll: boolean;
  temperature_override_enabled: boolean;
  query_history_type: QueryHistoryType;
  // CR-018 — Analytics & Usage Dashboard. Gate cho mục sidebar Usage/Query
  // History (xem `sections/sidebar/AdminSidebar.tsx`) — mặc định bật
  // (`true`) khi undefined, không gate cứng theo Enterprise nữa (backend
  // SOL-018 §5.6).
  usage_analytics_enabled?: boolean;

  deep_research_enabled?: boolean;
  multi_model_chat_enabled?: boolean;
  search_ui_enabled?: boolean;

  // Image processing settings
  image_extraction_and_analysis_enabled?: boolean;
  search_time_image_analysis_enabled?: boolean;
  image_analysis_max_size_mb?: number | null;

  // User Knowledge settings
  user_knowledge_enabled?: boolean;
  user_file_max_upload_size_mb?: number | null;
  file_token_count_threshold_k?: number | null;

  // Connector settings
  show_extra_connectors?: boolean;

  // Default Assistant settings
  disable_default_assistant?: boolean;

  // VNP Workplace Craft (Build Mode) feature flag
  workplace_craft_enabled?: boolean;

  // Whether EE features are unlocked (user has a valid enterprise license).
  // Controls UI visibility of EE features like user groups, analytics, RBAC.
  ee_features_enabled?: boolean;

  // Seat usage - populated when seat limit is exceeded
  seat_count?: number | null;
  used_seats?: number | null;

  // OpenSearch migration
  opensearch_indexing_enabled?: boolean;

  // Vector DB availability flag - false when DISABLE_VECTOR_DB is set.
  // When false, connectors, RAG search, document sets, and related features
  // are unavailable.
  vector_db_enabled?: boolean;

  // True when hooks are available: single-tenant deployment with HOOK_ENABLED=true.
  hooks_enabled?: boolean;

  // Application version from the WORKPLACE_VERSION env var on the server.
  version?: string | null;
  // Hard ceiling for user_file_max_upload_size_mb, derived from env var.
  max_allowed_upload_size_mb?: number;

  // Factory defaults for the restore button.
  default_user_file_max_upload_size_mb?: number;
  default_file_token_count_threshold_k?: number;
}

export enum NotificationType {
  PERSONA_SHARED = "persona_shared",
  REINDEX = "reindex",
  TRIAL_ENDS_TWO_DAYS = "two_day_trial_ending",
  ASSISTANT_FILES_READY = "assistant_files_ready",
  RELEASE_NOTES = "release_notes",
  FEATURE_ANNOUNCEMENT = "feature_announcement",
}

export interface Notification {
  // Internal string ID (backend: notification-service, no integer display_id
  // for this table — unlike chat_session/project/persona elsewhere).
  id: string;
  notif_type: string;
  title: string;
  description: string | null;
  dismissed: boolean;
  first_shown: string;
  last_shown: string;
  additional_data?: {
    persona_id?: number;
    link?: string;
    version?: string; // For release notes notifications
    [key: string]: any;
  };
}

export interface NavigationItem {
  link: string;
  icon?: string;
  svg_logo?: string;
  title: string;
}

export interface EnterpriseSettings {
  application_name: string | null;
  use_custom_logo: boolean;
  use_custom_logotype: boolean;
  logo_display_style: "logo_and_name" | "logo_only" | "name_only" | null;

  // custom navigation
  custom_nav_items: NavigationItem[];

  // custom Chat components
  custom_lower_disclaimer_content: string | null;
  custom_header_content: string | null;
  two_lines_for_chat_header: boolean | null;
  custom_popup_header: string | null;
  custom_popup_content: string | null;
  enable_consent_screen: boolean | null;
  consent_screen_prompt: string | null;
  show_first_visit_notice: boolean | null;
  custom_greeting_message: string | null;
}

export interface CombinedSettings {
  settings: Settings;
  enterpriseSettings: EnterpriseSettings | null;
  customAnalyticsScript: string | null;
  isMobile?: boolean;
  webVersion: string | null;
  webDomain: string | null;

  /**
   * NOTE (@raunakab):
   * Whether search mode is actually available to users.
   *
   * Prefer this over reading `settings.search_ui_enabled` directly.
   * `search_ui_enabled` only reflects the admin's *preference* — it does not
   * account for prerequisites like connectors being configured. This derived
   * flag combines the admin setting with runtime checks (e.g. connectors
   * exist) so consumers get a single, accurate boolean.
   */
  isSearchModeAvailable: boolean;
  settingsLoading: boolean;
}
